import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus,
  Car,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Eye,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const INSPECTION_TYPES = [
  { value: 'pre_trip', label: 'Pre-Trip' },
  { value: 'post_trip', label: 'Post-Trip' }
];

const DEFAULT_CHECKLIST = [
  { item: 'Brakes', status: 'pass' },
  { item: 'Steering', status: 'pass' },
  { item: 'Lights (Headlights, Brake, Turn)', status: 'pass' },
  { item: 'Tires & Wheels', status: 'pass' },
  { item: 'Horn', status: 'pass' },
  { item: 'Mirrors', status: 'pass' },
  { item: 'Windshield & Wipers', status: 'pass' },
  { item: 'Fluid Levels', status: 'pass' },
  { item: 'Seatbelts', status: 'pass' },
  { item: 'Emergency Equipment', status: 'pass' },
  { item: 'Exhaust System', status: 'pass' },
  { item: 'Fuel System', status: 'pass' }
];

export default function AdminVehicleInspections() {
  const { authAxios, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [inspections, setInspections] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_id: '',
    odometer_reading: '',
    inspection_type: 'pre_trip',
    project_id: '',
    checklist: [...DEFAULT_CHECKLIST],
    defects_noted: '',
    overall_status: 'pass',
    signature: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [inspRes, equipRes, projRes] = await Promise.all([
        authAxios.get('/forms/vehicle-inspections'),
        authAxios.get('/equipment'),
        authAxios.get('/projects')
      ]);
      setInspections(inspRes.data);
      // Filter for vehicles only
      setEquipment(equipRes.data.filter(e => e.equipment_type === 'vehicle'));
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      odometer_reading: '',
      inspection_type: 'pre_trip',
      project_id: '',
      checklist: [...DEFAULT_CHECKLIST],
      defects_noted: '',
      overall_status: 'pass',
      signature: ''
    });
  };

  const updateChecklistItem = (index, status) => {
    const newChecklist = [...formData.checklist];
    newChecklist[index].status = status;
    
    const hasFails = newChecklist.some(item => item.status === 'fail');
    setFormData({
      ...formData,
      checklist: newChecklist,
      overall_status: hasFails ? 'fail' : 'pass'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_id || !formData.odometer_reading || !formData.signature) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/forms/vehicle-inspections', {
        ...formData,
        odometer_reading: parseInt(formData.odometer_reading),
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null
      });
      toast.success('Vehicle inspection submitted');
      if (formData.overall_status === 'fail' && isAdmin) {
        toast.warning('Vehicle marked as Out of Service due to failed inspection');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inspection');
    } finally {
      setSubmitting(false);
    }
  };

  const exportPDF = async (inspectionId) => {
    try {
      const response = await authAxios.get(`/pdf/vehicle-inspection/${inspectionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vehicle_inspection_${inspectionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'pass') {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Pass</Badge>;
    }
    return <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-1" />Fail</Badge>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vehicle-inspections">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Vehicle Inspections</h1>
          <p className="text-stone-500 mt-1">Pre-trip and post-trip vehicle safety inspections</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-vehicle-inspection-btn">
              <Plus className="w-5 h-5 mr-2" />
              New Inspection
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Car className="w-5 h-5 text-emerald-600" />
                Vehicle Inspection Form
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle *</Label>
                  <Select value={formData.vehicle_id} onValueChange={(v) => setFormData({ ...formData, vehicle_id: v })}>
                    <SelectTrigger data-testid="vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipment.length === 0 ? (
                        <SelectItem value="no-vehicles" disabled>No vehicles available</SelectItem>
                      ) : (
                        equipment.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.name} ({e.equipment_id})</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Inspection Type *</Label>
                  <Select value={formData.inspection_type} onValueChange={(v) => setFormData({ ...formData, inspection_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INSPECTION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Odometer Reading *</Label>
                  <Input
                    type="number"
                    value={formData.odometer_reading}
                    onChange={(e) => setFormData({ ...formData, odometer_reading: e.target.value })}
                    placeholder="Current mileage"
                    required
                    data-testid="vehicle-odometer"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project (Optional)</Label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Inspection Checklist</Label>
                <div className="border rounded-lg divide-y">
                  {formData.checklist.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3">
                      <span className="font-medium text-sm">{item.item}</span>
                      <div className="flex items-center gap-2">
                        {['pass', 'fail', 'na'].map(status => (
                          <Button
                            key={status}
                            type="button"
                            size="sm"
                            variant={item.status === status ? 'default' : 'outline'}
                            className={item.status === status ? (
                              status === 'pass' ? 'bg-emerald-600' :
                              status === 'fail' ? 'bg-rose-600' : 'bg-stone-600'
                            ) : ''}
                            onClick={() => updateChecklistItem(idx, status)}
                          >
                            {status.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
                <span className="font-medium">Overall Status:</span>
                {getStatusBadge(formData.overall_status)}
                {formData.overall_status === 'fail' && (
                  <span className="text-sm text-rose-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {isAdmin ? 'Vehicle will be marked Out of Service' : 'Your admin will be alerted to this failure'}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <Label>Defects/Damage Noted</Label>
                <Textarea
                  value={formData.defects_noted}
                  onChange={(e) => setFormData({ ...formData, defects_noted: e.target.value })}
                  placeholder="Describe any defects, damage, or concerns..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Signature (Type Name) *</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  placeholder="Type your full name as signature"
                  required
                  data-testid="vehicle-signature"
                />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Inspection
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Inspections Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Car className="w-5 h-5 text-stone-400" />
            Vehicle Inspections ({inspections.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No vehicle inspections found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Odometer</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right sticky right-0 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inspections.map((insp) => (
                    <TableRow key={insp.id}>
                      <TableCell className="font-medium">{insp.vehicle_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{insp.inspection_type?.replace('_', '-')}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{insp.odometer_reading?.toLocaleString()} mi</TableCell>
                      <TableCell>{insp.inspector_name}</TableCell>
                      <TableCell>{insp.project_name || '-'}</TableCell>
                      <TableCell>{getStatusBadge(insp.overall_status)}</TableCell>
                      <TableCell>{fmtTz(insp.created_at, 'MMM d, yyyy h:mm a')}</TableCell>
                      <TableCell className="text-right space-x-1 sticky right-0 bg-white whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSelectedInspection(insp); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => exportPDF(insp.id)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              if (!window.confirm('Delete this vehicle inspection permanently?')) return;
                              try {
                                await authAxios.delete(`/forms/vehicle-inspections/${insp.id}`);
                                toast.success('Vehicle inspection deleted');
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                            }}
                            data-testid={`delete-vehicle-${insp.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Inspection Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vehicle Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Vehicle</p>
                  <p className="font-medium">{selectedInspection.vehicle_name}</p>
                </div>
                <div>
                  <p className="text-stone-500">Type</p>
                  <p className="font-medium capitalize">{selectedInspection.inspection_type?.replace('_', '-')}</p>
                </div>
                <div>
                  <p className="text-stone-500">Odometer</p>
                  <p className="font-medium">{selectedInspection.odometer_reading?.toLocaleString()} mi</p>
                </div>
                <div>
                  <p className="text-stone-500">Status</p>
                  {getStatusBadge(selectedInspection.overall_status)}
                </div>
              </div>
              <div>
                <p className="text-stone-500 text-sm mb-2">Checklist</p>
                <div className="border rounded-lg divide-y">
                  {selectedInspection.checklist?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 text-sm">
                      <span>{item.item}</span>
                      <Badge className={
                        item.status === 'pass' ? 'bg-emerald-100 text-emerald-700' :
                        item.status === 'fail' ? 'bg-rose-100 text-rose-700' :
                        'bg-stone-100 text-stone-600'
                      }>
                        {item.status.toUpperCase()}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              {selectedInspection.defects_noted && (
                <div>
                  <p className="text-stone-500 text-sm">Defects Noted</p>
                  <p className="text-sm">{selectedInspection.defects_noted}</p>
                </div>
              )}
              <div className="pt-4 border-t text-sm">
                <p className="text-stone-500">Signature</p>
                <p className="font-medium italic">{selectedInspection.signature}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
